/**
 * useNotifications — Read notifications from Supabase with Realtime (#647, #656)
 *
 * Fetches notifications for the current user and subscribes to
 * Realtime INSERT events for live updates.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getSupabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';

const log = createLogger('Notifications');

export interface NotificationRow {
  id: string;
  user_id: string;
  type: 'follow' | 'reaction' | 'comment' | 'repost' | 'mention' | 'system';
  actor_id: string | null;
  target_type: string | null;
  target_id: string | null;
  read: boolean;
  created_at: string;
}

interface UseNotificationsResult {
  notifications: NotificationRow[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refresh: () => void;
}

/**
 * Hook to fetch and subscribe to notifications for a user.
 * In mock mode, returns empty array.
 */
export function useNotifications(userId: string | null): UseNotificationsResult {
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef<ReturnType<ReturnType<typeof getSupabase>['channel']> | null>(null);
  const fetchIdRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    const id = ++fetchIdRef.current;
    setLoading(true);
    try {
      const sb = getSupabase();
      const { data, error } = await sb
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (id !== fetchIdRef.current) return; // stale
      if (error) {
        log.warn('Failed to fetch notifications', { err: error.message });
      } else {
        setNotifications((data ?? []) as NotificationRow[]);
      }
    } catch (err) {
      log.warn('Notification fetch threw', { err: String(err) });
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [userId]);

  // Initial fetch + Realtime subscription (#656)
  useEffect(() => {
    if (!userId) return;

    fetchNotifications();

    // Subscribe to new notifications via Supabase Realtime
    try {
      const sb = getSupabase();
      const channel = sb
        .channel(`notifications:${userId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            log.info('New notification received', { type: payload.new?.type });
            setNotifications(prev => [payload.new as NotificationRow, ...prev]);
          },
        )
        .subscribe((status) => {
          log.info('Notification channel status', { status });
        });

      channelRef.current = channel;
    } catch (err) {
      log.warn('Realtime subscription failed', { err: String(err) });
    }

    return () => {
      if (channelRef.current) {
        try {
          const sb = getSupabase();
          sb.removeChannel(channelRef.current);
        } catch { /* cleanup best-effort */ }
        channelRef.current = null;
      }
    };
  }, [userId, fetchNotifications]);

  const markAsRead = useCallback(async (id: string) => {
    if (!userId) return; // #680: removed dead isMockMode() guard
    try {
      const sb = getSupabase();
      await sb.from('notifications').update({ read: true }).eq('id', id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (err) {
      log.warn('Mark as read failed', { err: String(err) });
    }
  }, [userId]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    try {
      const sb = getSupabase();
      await sb.from('notifications').update({ read: true }).eq('user_id', userId).eq('read', false);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      log.warn('Mark all read failed', { err: String(err) });
    }
  }, [userId]);

  return {
    notifications,
    unreadCount: notifications.filter(n => !n.read).length,
    loading,
    markAsRead,
    markAllRead,
    refresh: fetchNotifications,
  };
}